import {head, isEqual} from 'lodash';
import {Key, ReactNode} from 'react';

import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ContactSourcesEnum} from '../../../libs/shared/core/src/models/api/contactModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {AccountModel} from '../../../src/models/api/accountModel';
import {RecipientContactMatchModel} from '../../../src/models/api/recipientMatchModel';
import {
  SmartMixClassnamesEnum,
  SmartMixContactMatchModel,
  SmartMixMatchModel,
  SmartMixTeammateMatchModel,
} from '../../../src/models/api/smartMixMatchModel';
import {TagModel} from '../../../src/models/api/tagModel';
import {ModifiersEnum} from './searchModifiersHelpers';
import {
  buildSearchToken,
  findActiveToken,
  findActiveTokens,
  SearchModifierToken,
  splitSearchQuery,
} from './searchQueryHelpers';

/*
 * Types.
 */

interface InboxesSearchDropdownItemBase {
  value: string;
  fuzzyQuery: string;
  onSelectOptions?: SuggestionSelectHandlerOptions;
}

export interface InboxesSearchDropdownRecentQuery extends InboxesSearchDropdownItemBase {}

export interface InboxesSearchDropdownSuggestionBase extends InboxesSearchDropdownItemBase {
  key?: Key;
  modifier: ModifiersEnum;
  score?: number | undefined;
  isSmartMixSuggestion?: boolean;
}

interface InboxesSearchDropdownSimpleSuggestion extends InboxesSearchDropdownSuggestionBase {
  label?: string;
}

interface InboxesSearchDropdownTutorialSuggestion extends InboxesSearchDropdownSuggestionBase {
  valuePlaceholder: ReactNode;
}

export interface InboxesSearchDropdownTeammateSuggestion extends InboxesSearchDropdownSuggestionBase {
  teammate: TeammateModel;
}

export interface InboxesSearchDropdownContactMatchSuggestion extends InboxesSearchDropdownSuggestionBase {
  contactMatch: RecipientContactMatchModel;
}

interface InboxesSearchDropdownAccountSuggestion extends InboxesSearchDropdownSuggestionBase {
  account: AccountModel;
}

interface InboxesSearchDropdownDynamicObjectSuggestion extends InboxesSearchDropdownSimpleSuggestion {
  dynamicObjectType: string;
}

interface InboxesSearchDropdownTagSuggestion extends InboxesSearchDropdownSuggestionBase {
  tag: TagModel;
  tagsPath: ReadonlyArray<TagModel>;
  shouldShowDeduplication: boolean;
}

export type InboxesSearchDropdownSuggestion =
  | InboxesSearchDropdownSimpleSuggestion
  | InboxesSearchDropdownTutorialSuggestion
  | InboxesSearchDropdownTeammateSuggestion
  | InboxesSearchDropdownContactMatchSuggestion
  | InboxesSearchDropdownAccountSuggestion
  | InboxesSearchDropdownDynamicObjectSuggestion
  | InboxesSearchDropdownTagSuggestion;

type SuggestionSelectHandlerTrackingOptions = {
  query?: string;

  modifierHelperType?: ModifiersEnum;
  isRecentQuery?: boolean;

  isModifierSuggestion?: boolean;
  modifierSuggestionType?: ModifiersEnum;

  isConversationSuggestion?: boolean;
  topResultsConversationId?: number;
  conversationClassificationLabels?: ReadonlyArray<string>;

  isSmartMixItem?: boolean;
  smartMixType?: SmartMixClassnamesEnum;
  smartMixValue?: string;
  smartMixIndex?: number;
  smartMixNamespace?: string;
  smartMixScore?: number;
  smartMixResourceId?: number;
  isClientSideFiltering?: boolean;
  isEnhancingContactsAutosuggest?: boolean;
};

export type SuggestionSelectHandlerOptions = SuggestionSelectHandlerTrackingOptions & {
  insertWhiteSpace?: boolean;
  replaceWholeQuery?: boolean;
  shouldSaveOriginalQuery?: boolean;
};

export type SuggestionSelectHandler = (
  text: string,
  selectKey?: Key,
  options?: SuggestionSelectHandlerOptions,
) => void;

export interface SearchSuggestionsBaseProps {
  query?: string;
  onSuggestionSelect: SuggestionSelectHandler;
}

/*
 * Constants.
 */

export const maxSearchSuggestions = 5;

export const fuzzySortBaseOptions = {
  allowTypo: true,
  limit: maxSearchSuggestions,
  threshold: -20,
};

const TypeValues = ['email', 'facebook', 'googleplay', 'intercom', 'sms', 'tweet', 'tweet_dm'];
const HasValues = ['attachments', 'calendar', 'draft', 'no-tags'];
const IsValues = [
  'assigned',
  'archived',
  'discussion',
  'open',
  'shared-with-me',
  'snoozed',
  'spam',
  'starred',
  'subscribed',
  'trashed',
  'unassigned',
  'unread',
  'unreplied',
  'unsubscribed',
];
const InValues = ['all', 'comments', 'messages', 'attachments'];
const CommonDatesValues = ['last-week'];

const CommonDatesPlaceholders = ['add date (yyyy-mm-dd)', 'add date (yyyy-mm)'];

/*
 * Helpers.
 */

export function insertSuggestionInPlace(
  query: string,
  newTokenText: string,
  offset = query.length,
  shouldInsertBlank = false,
): string {
  const searchQueryParts = splitSearchQuery(query);

  const activeTokens = findActiveTokens(query, offset);

  if (activeTokens.length > 1) {
    let shouldSkipText = false;

    return searchQueryParts
      .map(({text, token}, partIndex, allParts) => {
        if (token) {
          const activeTokenIndex = activeTokens.findIndex((activeToken) => isEqual(token, activeToken));

          // If we hit the last active token, stop skipping text and compute if we need to add a blank.
          if (activeTokenIndex === activeTokens.length - 1) {
            shouldSkipText = false;
            return shouldInsertBlank && partIndex === allParts.length - 1 ? ' ' : '';
          }

          if (activeTokenIndex !== -1) {
            // If we hit the first active token, we need to check if subsequent tokens are active.
            if (activeTokenIndex === 0) {
              // As we matched the first active token, we're sure that a subsequent query part with a token will be defined.
              const nextQueryPartWithToken = head(
                allParts.slice(partIndex + 1).filter((part) => isDefined(part.token)),
              );

              // If the next query part's token equals the next active token, we need to replace and skip text until we match the last active token.
              if (
                nextQueryPartWithToken &&
                isEqual(nextQueryPartWithToken.token, activeTokens[activeTokenIndex + 1])
              ) {
                shouldSkipText = true;
                return newTokenText;
              }

              // If this is not the case, it means we did not match all active tokens.
              // In that case, return the original text.
              return text;
            }

            return '';
          }
        }

        return shouldSkipText ? '' : text;
      })
      .join('');
  }

  const activeToken = findActiveToken(query, offset);

  return searchQueryParts
    .map(({text, token}, partIndex, allParts) => {
      if (token && isEqual(token, activeToken)) {
        const maybeBlank = shouldInsertBlank && partIndex === allParts.length - 1 ? ' ' : '';
        return newTokenText + maybeBlank;
      }

      return text;
    })
    .join('');
}

export function shouldInsertWhitespace(value: string) {
  const token = buildSearchToken(value);
  return Boolean(token && token.value);
}

export function getPropertiesSuggestions(
  modifierToken: SearchModifierToken,
): ReadonlyArray<InboxesSearchDropdownSuggestion> {
  const {modifier, value} = modifierToken;

  const values = getPropertiesValues(modifier);
  const placeholders = getPropertiesPlaceholders(modifier);

  const valuesSuggestions: ReadonlyArray<InboxesSearchDropdownSuggestion> = values.map((v) => ({
    modifier,
    value: `${modifier}:${v}`,
    fuzzyQuery: value ?? '',
  }));

  const placeholderSuggestions: ReadonlyArray<InboxesSearchDropdownSuggestion> = placeholders.map(
    (placeholder) => ({
      key: `${modifier}-${placeholder}`,
      modifier,
      value: `${modifier}:`,
      valuePlaceholder: placeholder,
      fuzzyQuery: value ?? '',
    }),
  );

  return [...valuesSuggestions, ...placeholderSuggestions];
}

function getPropertiesValues(modifier: ModifiersEnum | undefined): Array<string> {
  switch (modifier) {
    case ModifiersEnum.TYPE:
    case ModifiersEnum.CHANNEL_TYPE:
      return TypeValues;

    case ModifiersEnum.HAS:
      return HasValues;

    case ModifiersEnum.IS:
      return IsValues;

    case ModifiersEnum.IN:
      return InValues;

    case ModifiersEnum.BEFORE:
    case ModifiersEnum.DURING:
      return ['today', ...CommonDatesValues];

    case ModifiersEnum.AFTER:
      return ['yesterday', ...CommonDatesValues];

    default:
      throw new Error('Property modifier does not exist');
  }
}

function getPropertiesPlaceholders(modifier: ModifiersEnum | undefined): Array<string> {
  switch (modifier) {
    case ModifiersEnum.BEFORE:
    case ModifiersEnum.DURING:
    case ModifiersEnum.AFTER:
      return CommonDatesPlaceholders;

    default:
      return [];
  }
}

export function maybePromoteTeammateSuggestions(
  contactSuggestions: ReadonlyArray<SmartMixContactMatchModel>,
  suggestions: ReadonlyArray<SmartMixMatchModel>,
) {
  const personSuggestions: Array<SmartMixContactMatchModel | SmartMixTeammateMatchModel> = [];
  const promotedTeammateSuggestions: Array<SmartMixTeammateMatchModel> = [];

  const teammateSuggestions = suggestions.filter(
    (s): s is SmartMixTeammateMatchModel => s.class === SmartMixClassnamesEnum.TEAMMATE,
  );

  for (const suggestedContact of contactSuggestions) {
    // If the source is not `email`, add the contact suggestion.
    if (suggestedContact.recipient.source !== ContactSourcesEnum.EMAIL) {
      personSuggestions.push(suggestedContact);
      continue;
    }

    // Check if a teammate has the same email.
    const maybeTeammateSuggestion = teammateSuggestions.find((t) => t.value === suggestedContact.value);

    // If this is the case, add the teammate suggestion.
    if (maybeTeammateSuggestion) {
      personSuggestions.push(maybeTeammateSuggestion);
      // Keep track of the promoted teammate, so that we can remove it from the other suggestions.
      promotedTeammateSuggestions.push(maybeTeammateSuggestion);
      continue;
    }

    // Otherwise, add the contact suggestion.
    personSuggestions.push(suggestedContact);
  }

  return {
    personSuggestions,
    // Filter out teammates that were included in persons.
    suggestions: suggestions.filter(
      (suggestion) =>
        suggestion.class !== SmartMixClassnamesEnum.TEAMMATE ||
        !promotedTeammateSuggestions.some((s) => suggestion.value === s.value),
    ),
  };
}
